import { useEffect, useRef, useState } from "react";
import { CloseIcon } from "../ui";
import { useModalChrome } from "../lib/modalUx";

// In-app confirm / prompt dialogs. Replaces window.confirm / window.prompt
// so iPhone Safari doesn't steal focus, scroll under, or ignore the safe
// area. Imperative API mirrors the built-ins (returns a Promise) so call
// sites read the same way.

interface ConfirmSpec {
  kind: "confirm";
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  resolve: (ok: boolean) => void;
}
interface PromptSpec {
  kind: "prompt";
  title: string;
  body?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  resolve: (value: string | null) => void;
}

type Spec = ConfirmSpec | PromptSpec;

const listeners = new Set<(s: Spec) => void>();

export function confirmAction(opts: {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const spec: ConfirmSpec = { kind: "confirm", ...opts, resolve };
    if (listeners.size === 0) {
      // Fallback for early call sites (should not happen once host mounts).
      resolve(window.confirm(opts.title));
      return;
    }
    listeners.forEach((l) => l(spec));
  });
}

export function promptText(opts: {
  title: string;
  body?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const spec: PromptSpec = { kind: "prompt", ...opts, resolve };
    if (listeners.size === 0) {
      resolve(window.prompt(opts.title, opts.defaultValue ?? ""));
      return;
    }
    listeners.forEach((l) => l(spec));
  });
}

const backdrop =
  "fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4";
const panel =
  "flex w-full max-w-md flex-col gap-4 rounded-t-3xl bg-paper p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-card sm:rounded-3xl";
const primaryBtn =
  "rounded-full bg-grad-cta px-4 py-2.5 text-sm font-bold text-white shadow-card transition hover:opacity-95 disabled:opacity-50 min-h-[44px]";
const dangerBtn =
  "rounded-full bg-coral px-4 py-2.5 text-sm font-bold text-white shadow-card transition hover:opacity-95 disabled:opacity-50 min-h-[44px]";
const cancelBtn =
  "rounded-full px-4 py-2.5 text-sm font-bold text-ink/70 ring-1 ring-inset ring-ink/10 hover:bg-ink/5 min-h-[44px]";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-ink/60";

export function ConfirmHost() {
  const [spec, setSpec] = useState<Spec | null>(null);
  const [value, setValue] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onOpen = (s: Spec) => {
      setSpec(s);
      setValue(s.kind === "prompt" ? s.defaultValue ?? "" : "");
    };
    listeners.add(onOpen);
    return () => {
      listeners.delete(onOpen);
    };
  }, []);

  const cancel = () => {
    if (!spec) return;
    if (spec.kind === "prompt") spec.resolve(null);
    else spec.resolve(false);
    setSpec(null);
  };
  const confirm = () => {
    if (!spec) return;
    if (spec.kind === "prompt") spec.resolve(value);
    else spec.resolve(true);
    setSpec(null);
  };

  // Shared modal chrome: background scroll-lock, Android/iOS back-gesture →
  // cancel, and a Tab focus-trap inside the panel (a destructive confirm
  // mustn't let Tab wander onto the page behind it). Same hook every other
  // modal uses.
  useModalChrome(!!spec, cancel, panelRef);

  // Escape cancels; land keyboard focus on Cancel for a plain confirm so
  // Enter doesn't default onto the destructive action.
  useEffect(() => {
    if (!spec) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    };
    window.addEventListener("keydown", onKey);
    if (spec.kind === "confirm") {
      requestAnimationFrame(() => cancelBtnRef.current?.focus());
    }
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  if (!spec) return null;

  const destructive = spec.kind === "confirm" && spec.destructive;

  return (
    <div className={backdrop} onClick={cancel}>
      <div
        ref={panelRef}
        tabIndex={-1}
        className={panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={spec.title}
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={labelCls}>
              {spec.kind === "prompt" ? "Enter a value" : "Confirm"}
            </p>
            <h2 className="font-display text-lg font-bold text-ink">
              {spec.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={cancel}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-ink/60 hover:bg-ink/5"
            aria-label="Cancel"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </header>
        {spec.body ? (
          <p className="text-sm text-ink/70">{spec.body}</p>
        ) : null}
        {spec.kind === "prompt" ? (
          <input
            type="text"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={spec.placeholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirm();
              }
            }}
            className="min-h-[44px] rounded-xl bg-paper px-3 py-2.5 text-base text-ink ring-1 ring-inset ring-ink/15 focus:outline-none focus:ring-2 focus:ring-aqua-deep"
          />
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            ref={cancelBtnRef}
            onClick={cancel}
            className={cancelBtn}
          >
            {spec.cancelLabel ?? "Cancel"}
          </button>
          <button
            type="button"
            onClick={confirm}
            className={destructive ? dangerBtn : primaryBtn}
          >
            {spec.confirmLabel ?? (spec.kind === "prompt" ? "Save" : "OK")}
          </button>
        </div>
      </div>
    </div>
  );
}
